// src/pages/creator/CreateCourse.jsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Save, ArrowLeft, Upload, DollarSign, BookOpen, Users, Clock, Star, Tag,
  Target, AlertCircle, CheckCircle, Image as ImageIcon, Plus, X
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { firestoreService } from '../../../services/firestoreService';
import { useAuth } from '../../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { FileUpload } from '../../common/FileUpload';
import toast from 'react-hot-toast';

const CreateCourse = ({ bookId, onNavigate }) => {
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [learningObjectives, setLearningObjectives] = useState(['']);
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      level: 'Beginner',
      isPremium: false,
      price: 0,
      duration: 60,
      durationUnit: 'minutes',
      tags: '',
      prerequisites: '',
      targetAudience: '',
      language: 'English'
    }
  });

  const isPremium = watch('isPremium');
  const selectedCategory = watch('category');

  const categories = [
    'Business & Finance',
    'Digital Marketing',
    'Programming & Tech',
    'Career Development',
    'Entrepreneurship',
    'Personal Development',
    'Design & Creative',
    'Data Science',
    'Health & Wellness',
    'Languages',
    'Photography',
    'Communication Skills',
    'Custom Category'
  ];

  const levels = [
    { value: 'Beginner', label: 'Beginner', description: 'No prior experience needed' },
    { value: 'Intermediate', label: 'Intermediate', description: 'Some basic knowledge required' },
    { value: 'Advanced', label: 'Advanced', description: 'Extensive experience required' },
    { value: 'All Levels', label: 'All Levels', description: 'Suitable for everyone' }
  ];

  const durationUnits = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' }
  ];

  const languages = [
    'English', 'Spanish', 'French', 'German', 'Portuguese', 
    'Italian', 'Dutch', 'Japanese', 'Korean', 'Chinese'
  ];

  const steps = [
    { number: 1, title: 'Basic Information', description: 'Course title, description, and category' },
    { number: 2, title: 'Course Details', description: 'Learning objectives, prerequisites, and target audience' },
    { number: 3, title: 'Pricing & Media', description: 'Set pricing and upload course thumbnail' },
    { number: 4, title: 'Review & Submit', description: 'Review all information before submission' }
  ];

  // Learning Objectives Management
  const addLearningObjective = () => {
    setLearningObjectives([...learningObjectives, '']);
  };

  const removeLearningObjective = (index) => {
    if (learningObjectives.length > 1) {
      setLearningObjectives(learningObjectives.filter((_, i) => i !== index));
    }
  };

  const updateLearningObjective = (index, value) => {
    const updated = [...learningObjectives];
    updated[index] = value;
    setLearningObjectives(updated);
  };

  // Category Management
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'Custom Category') {
      setShowCustomCategory(true);
      setValue('category', '');
    } else {
      setShowCustomCategory(false);
      setCustomCategory('');
      setValue('category', value);
    }
  };

  const handleCustomCategoryChange = (e) => {
    const value = e.target.value;
    setCustomCategory(value);
    setValue('category', value);
  };

  const handleThumbnailUpload = async (file) => {
    try {
      setThumbnailFile(file);
      const uploadPath = `courserve-courses/thumbnails/${user.uid}_${Date.now()}`;
      
      const downloadURL = await firestoreService.uploadFile(
        file, 
        uploadPath, 
        (progress) => setThumbnailUploadProgress(progress)
      );
      
      setThumbnailUrl(downloadURL);
      setValue('thumbnail', downloadURL);
      toast.success('Thumbnail uploaded successfully!');
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Failed to upload thumbnail');
    }
  };

  const handleThumbnailRemove = () => {
    setThumbnailFile(null);
    setThumbnailUrl('');
    setThumbnailUploadProgress(0);
    setValue('thumbnail', '');
  };

  const validateStep = (step) => {
    const data = watch();
    
    switch (step) {
      case 1:
        return !!(data.title?.trim() && 
                 data.description?.trim() && 
                 data.shortDescription?.trim() && 
                 data.category);
      case 2:
        return !!(learningObjectives.some(obj => obj.trim()) && 
                 data.targetAudience?.trim());
      case 3:
        return !isPremium || (isPremium && data.price > 0);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please fill in all required fields before continuing');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      // Process tags
      const tagsArray = data.tags ? 
        data.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [];
      
      // Process learning objectives
      const objectivesArray = learningObjectives.filter(obj => obj.trim());

      // Convert duration to minutes for storage
      let durationInMinutes = parseInt(data.duration);
      switch (data.durationUnit) {
        case 'hours':
          durationInMinutes *= 60;
          break;
        case 'days':
          durationInMinutes *= 60 * 24;
          break;
        case 'weeks':
          durationInMinutes *= 60 * 24 * 7;
          break;
        default:
          break;
      }

      const courseData = {
        ...data,
        creatorId: user.uid,
        creatorName: user.displayName || user.email,
        tags: tagsArray,
        learningObjectives: objectivesArray,
        estimatedDuration: durationInMinutes,
        displayDuration: {
          value: parseInt(data.duration),
          unit: data.durationUnit
        },
        price: isPremium ? parseFloat(data.price) : 0,
        thumbnail: thumbnailUrl || null,
        status: 'draft',
        isPublished: false,
        featured: false,
        enrollmentCount: 0,
        rating: 0,
        ratingCount: 0,
        views: 0,
        lessonCount: 0,
        completionCount: 0
      };

      const courseId = await courseService.createCourse(bookId, courseData);
      
      toast.success('Course created successfully!');
      
      // STATE-BASED NAVIGATION
      onNavigate('creator-edit', { courseId });
      
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('Failed to create course');
    } finally {
      setSaving(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step.number 
                ? 'bg-violet-600 border-violet-600 text-white' 
                : 'border-gray-300 text-gray-500'
            }`}>
              {currentStep > step.number ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <span className="text-sm font-medium">{step.number}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-24 h-1 ml-4 ${
                currentStep > step.number ? 'bg-violet-600' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {steps[currentStep - 1].title}
        </h3>
        <p className="text-gray-600">{steps[currentStep - 1].description}</p>
      </div>
    </div>
  );

  const renderStep1 = () => {
    const selectedLevel = watch('level');
    
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-6"
      >
        {/* Course Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Title *
          </label>
          <input
            {...register('title', { required: true })}
            type="text"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your course title"
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-600">Course title is required</p>
          )}
        </div>

        {/* Short Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Short Description *
          </label>
          <input
            {...register('shortDescription', { required: true })}
            type="text"
            maxLength={120}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description (120 characters max)"
          />
          {errors.shortDescription && (
            <p className="mt-1 text-sm text-red-600">Short description is required</p>
          )}
        </div>

        {/* Full Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Description *
          </label>
          <textarea
            {...register('description', { required: true })}
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Detailed course description..."
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">Description is required</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            {...register('category', { required: true })}
            onChange={handleCategoryChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a category</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-600">Category is required</p>
          )}
        </div>

        {/* Custom Category Input */}
        {showCustomCategory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Category Name *
            </label>
            <input
              type="text"
              value={customCategory}
              onChange={handleCustomCategoryChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter custom category name"
            />
          </motion.div>
        )}

        {/* Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Level *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {levels.map(level => (
              <label
                key={level.value}
                className={`relative flex cursor-pointer rounded-lg border-2 p-4 transition-all ${
                  selectedLevel === level.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400'
                }`}
              >
                <input
                  {...register('level')}
                  type="radio"
                  value={level.value}
                  className="sr-only"
                />
                <div className="flex w-full items-center justify-between">
                  <div className="text-sm">
                    <p className={`font-medium ${
                      selectedLevel === level.value ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      {level.label}
                    </p>
                    <p className={`${
                      selectedLevel === level.value ? 'text-blue-700' : 'text-gray-500'
                    }`}>
                      {level.description}
                    </p>
                  </div>
                  {selectedLevel === level.value && (
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language *
          </label>
          <select
            {...register('language', { required: true })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {languages.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>
      </motion.div>
    );
  };

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Learning Objectives */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Learning Objectives *
          </label>
          <button
            type="button"
            onClick={addLearningObjective}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Objective
          </button>
        </div>
        <div className="space-y-3">
          {learningObjectives.map((objective, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={objective}
                onChange={(e) => updateLearningObjective(index, e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="What will students learn?"
              />
              {learningObjectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLearningObjective(index)}
                  className="p-3 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Prerequisites */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Prerequisites
        </label>
        <textarea
          {...register('prerequisites')}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="What should students know before taking this course?"
        />
      </div>

      {/* Target Audience */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Audience *
        </label>
        <textarea
          {...register('targetAudience', { required: true })}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Who is this course for?"
        />
        {errors.targetAudience && (
          <p className="mt-1 text-sm text-red-600">Target audience is required</p>
        )}
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags (comma-separated)
        </label>
        <input
          {...register('tags')}
          type="text"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g., react, javascript, web development"
        />
      </div>

      {/* Estimated Duration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Estimated Duration *
        </label>
        <div className="grid grid-cols-2 gap-4">
          <input
            {...register('duration', { required: true, min: 1 })}
            type="number"
            min="1"
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Duration"
          />
          <select
            {...register('durationUnit')}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {durationUnits.map(unit => (
              <option key={unit.value} value={unit.value}>{unit.label}</option>
            ))}
          </select>
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Pricing */}
      <div>
        <label className="flex items-center gap-2 mb-4">
          <input
            {...register('isPremium')}
            type="checkbox"
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">
            This is a premium course
          </span>
        </label>

        {isPremium && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Price (R) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-md"> R </span>
              <input
                {...register('price', { required: isPremium, min: 0.01 })}
                type="number"
                step="0.01"
                min="0"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
              />
            </div>
            {errors.price && (
              <p className="mt-1 text-sm text-red-600">Price must be greater than 0</p>
            )}
          </motion.div>
        )}
      </div>

      {/* Thumbnail Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Course Thumbnail
        </label>
        {!thumbnailUrl ? (
          <FileUpload
            onFileSelect={handleThumbnailUpload}
            accept="image/*"
            maxSize={5}
            label="Upload Thumbnail"
            icon={ImageIcon}
          />
        ) : (
          <div className="relative">
            <img
              src={thumbnailUrl}
              alt="Course thumbnail"
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={handleThumbnailRemove}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {thumbnailUploadProgress > 0 && thumbnailUploadProgress < 100 && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-violet-600 h-2 rounded-full transition-all"
                style={{ width: `${thumbnailUploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderStep4 = () => {
    const data = watch();
    
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-6"
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Review Your Course
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Please review all information before submitting. You can edit these details later.
              </p>
            </div>
          </div>
        </div>

        {/* Review Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500">Title</h4>
            <p className="mt-1 text-lg font-semibold text-gray-900">{data.title}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500">Category</h4>
            <p className="mt-1 text-gray-900">{data.category}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500">Level</h4>
            <p className="mt-1 text-gray-900">{data.level}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500">Learning Objectives</h4>
            <ul className="mt-1 list-disc list-inside space-y-1">
              {learningObjectives.filter(obj => obj.trim()).map((obj, index) => (
                <li key={index} className="text-gray-900">{obj}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500">Pricing</h4>
            <p className="mt-1 text-gray-900">
              {isPremium ? `$${parseFloat(data.price).toFixed(2)}` : 'Free'}
            </p>
          </div>

          {thumbnailUrl && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Thumbnail</h4>
              <img
                src={thumbnailUrl}
                alt="Course thumbnail"
                className="w-48 h-32 object-cover rounded-lg"
              />
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          type="button"
          onClick={() => onNavigate('creator')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Course</h1>
          <p className="text-gray-600">Fill in the details to create your course</p>
        </div>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              Next Step
            </button>
          ) : (
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Creating...' : 'Create Course'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateCourse;